<template>
  <div v-if="items.length > 0" class="composer-attachments" data-test-id="chat-attachments">
    <button
      type="button"
      class="composer-attachments__toggle"
      :aria-expanded="isExpandedRef"
      :aria-controls="listId"
      data-test-id="chat-attachments-toggle"
      @click="isExpandedRef = !isExpandedRef"
    >
      <Paperclip class="size-4 shrink-0" aria-hidden="true" />
      <span data-test-id="chat-attachments-summary">
        <span class="composer-attachments__count">{{ countLabelRef }}</span>
        <span v-if="detailsLabelRef" class="composer-attachments__details"> · {{ detailsLabelRef }}</span>
      </span>
      <span class="sr-only">{{ isExpandedRef ? t('chat.attachments.hide') : t('chat.attachments.show') }}</span>
      <ChevronUp v-if="isExpandedRef" class="composer-attachments__chevron size-4" aria-hidden="true" />
      <ChevronDown v-else class="composer-attachments__chevron size-4" aria-hidden="true" />
    </button>

    <ul
      v-show="isExpandedRef"
      :id="listId"
      class="composer-attachments__list"
      :aria-label="t('chat.attachments.label')"
    >
      <li
        v-for="item in items"
        :key="item.id"
        data-test-id="chat-file"
        :class="[
          'composer-attachments__item',
          {
            'file-uploading-container--valid': item.status === 'ready',
            'composer-attachments__item--error': item.status === 'error',
          },
        ]"
        :data-status="item.status"
      >
        <span class="composer-attachments__ext" aria-hidden="true">
          <CircleAlert v-if="item.status === 'error'" class="size-4" />
          <template v-else>{{ getExtensionLabel(item) }}</template>
        </span>
        <span class="composer-attachments__name">{{ item.name }}</span>

        <template v-if="item.status === 'uploading'">
          <span
            class="composer-attachments__bar"
            role="progressbar"
            :aria-label="t('chat.attachments.uploadingFile', { name: item.name })"
            aria-valuemin="0"
            aria-valuemax="100"
            :aria-valuenow="item.percentage ?? 0"
            data-test-id="chat-file-loading"
          >
            <span :style="{ width: `${item.percentage ?? 0}%` }"></span>
          </span>
          <span class="composer-attachments__meta">{{ item.percentage ?? 0 }} %</span>
        </template>
        <template v-else-if="item.status === 'error'">
          <span class="composer-attachments__meta composer-attachments__meta--error">
            {{ t('chat.attachments.failedFile') }}
          </span>
          <button
            type="button"
            class="composer-attachments__action composer-attachments__action--error"
            :aria-label="t('chat.attachments.retry', { name: item.name })"
            data-test-id="chat-file-retry-btn"
            @click="emit('retry', item.id)"
          >
            <RotateCcw class="size-4" aria-hidden="true" />
          </button>
        </template>
        <template v-else>
          <span v-if="item.size" class="composer-attachments__meta">{{ formatSize(item.size) }}</span>
          <span class="composer-attachments__ready" :aria-label="t('chat.attachments.ready')" role="img">
            <Check class="size-4" aria-hidden="true" />
          </span>
        </template>

        <button
          type="button"
          class="composer-attachments__action"
          :aria-label="t('chat.conversation.removeFile', { name: item.name })"
          data-test-id="chat-file-remove-btn"
          @click="emit('remove', item.id)"
        >
          <X class="size-4" aria-hidden="true" />
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { Check, ChevronDown, ChevronUp, CircleAlert, Paperclip, RotateCcw, X } from 'lucide-vue-next'
import { computed, ref, useId, watch } from 'vue'

import { formatFileSize, getExtensionLabel } from '@/features/chat/utils/chat-file-format'
import {
  COMPOSER_ATTACHMENTS_COLLAPSE_AFTER,
  type ComposerAttachmentType,
  getComposerAttachmentsSummary,
} from '@/features/chat/utils/composer-attachments'
import { useI18n } from '@/lib/i18n'

const props = defineProps<{ items: ComposerAttachmentType[] }>()
const emit = defineEmits<{ remove: [id: string]; retry: [id: string] }>()

const { t, locale } = useI18n()
const listId = `composer-attachments-${useId()}`

const isExpandedRef = ref(true)
const summaryRef = computed(() => getComposerAttachmentsSummary(props.items))

// depliee par defaut ; repliee une fois quand la liste depasse 5 fichiers
watch(
  () => props.items.length,
  (count, previous) => {
    if (count > COMPOSER_ATTACHMENTS_COLLAPSE_AFTER && (previous ?? 0) <= COMPOSER_ATTACHMENTS_COLLAPSE_AFTER) {
      isExpandedRef.value = false
    }
    if (count === 0) isExpandedRef.value = true
  },
)

const countLabelRef = computed(() =>
  summaryRef.value.total === 1
    ? t('chat.attachments.countOne')
    : t('chat.attachments.countOther', { count: summaryRef.value.total }),
)
const detailsLabelRef = computed(() =>
  [
    summaryRef.value.uploading > 0 ? t('chat.attachments.uploading', { count: summaryRef.value.uploading }) : '',
    summaryRef.value.failed > 0 ? t('chat.attachments.failed', { count: summaryRef.value.failed }) : '',
  ]
    .filter(Boolean)
    .join(' · '),
)

function formatSize(bytes: number) {
  // unites en dur : les traductions passent par une mise en majuscule automatique
  return formatFileSize(
    bytes,
    String(locale.value),
    String(locale.value).startsWith('fr')
      ? { b: 'octets', kb: 'Ko', mb: 'Mo', gb: 'Go' }
      : { b: 'B', kb: 'KB', mb: 'MB', gb: 'GB' },
  )
}
</script>

<style lang="scss">
@import '@/styles/utils';

// texte d'erreur a 4,5:1 sur fond blanc
$attachment-error: #b42f2f;

.composer-attachments {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: calc($s * 0.75);
  border: 1px solid var(--border);
  border-radius: 8px;
  background-color: var(--background);

  &__toggle {
    display: flex;
    align-items: center;
    gap: $s;
    min-height: 2.25rem;
    padding: calc($s * 0.75) $s;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--foreground);
    font: inherit;
    font-size: 0.8125rem;
    text-align: left;
    cursor: pointer;

    &:hover {
      background-color: var(--muted);
    }

    &:focus-visible {
      outline: 2px solid var(--ring);
      outline-offset: 1px;
    }
  }

  &__count {
    font-weight: 600;
  }

  &__details {
    color: var(--muted-foreground);
  }

  &__chevron {
    margin-left: auto;
    color: var(--muted-foreground);
  }

  &__list {
    display: flex;
    flex-direction: column;
    margin: 0;
    padding: 0;
    list-style: none;
    max-height: 13.5rem;
    overflow-y: auto;
  }

  &__item {
    display: flex;
    align-items: center;
    gap: calc($s * 1.25);
    padding: calc($s * 0.75) $s;
    border-radius: 6px;
  }

  &__ext {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    flex-shrink: 0;
    border-radius: 6px;
    background-color: var(--muted);
    color: var(--muted-foreground);
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  &__item--error &__ext {
    background-color: color-mix(in srgb, var(--destructive) 10%, var(--background));
    color: $attachment-error;
  }

  &__name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    font-size: 0.8125rem;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__bar {
    display: block;
    width: 7.5rem;
    height: 4px;
    flex-shrink: 0;
    overflow: hidden;
    border-radius: 9999px;
    background-color: var(--muted);

    span {
      display: block;
      height: 100%;
      background-color: var(--primary);
      transition: width 0.2s ease;
    }
  }

  &__meta {
    flex-shrink: 0;
    font-size: 0.75rem;
    font-variant-numeric: tabular-nums;
    color: var(--muted-foreground);
    white-space: nowrap;

    &--error {
      color: $attachment-error;
    }
  }

  &__ready {
    display: flex;
    color: var(--success);
  }

  &__action {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    flex-shrink: 0;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--muted-foreground);
    cursor: pointer;

    &:hover {
      background-color: var(--muted);
      color: var(--foreground);
    }

    &:focus-visible {
      outline: 2px solid var(--ring);
      outline-offset: 1px;
    }

    &--error {
      color: $attachment-error;
    }
  }

  // colonne etroite : la barre d'avancement laisse la place au nom
  @include mq('<tablet') {
    &__bar {
      width: 3.5rem;
    }
  }
}
</style>
